import { useCallback, useEffect, useRef, useState, type FormEvent } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { toApiError, type ApiError } from '../../api/client';
import { billingApi, ownerPropertyApi } from '../../api/endpoints';
import type {
  ListingPlan,
  PaymentRequirement,
  Photo,
  PropertyDetail,
  Room,
  RoomType,
  SharingType,
  Subscription,
} from '../../api/types';
import { Checkbox, ConfirmDialog, Field } from '../../components/Common';
import { Checkout } from '../../components/Checkout';
import { ErrorState, InlineError, InlineSuccess, TableSkeleton } from '../../components/States';
import { availabilityLabel, date, humanise, money, relativeTime, statusLabel } from '../../utils/format';
import { IconClose } from '../../components/Icons';

type Tab = 'overview' | 'rooms' | 'photos' | 'availability' | 'billing';

const sharingBedCounts: Partial<Record<SharingType, number>> = {
  Single: 1,
  TwoSharing: 2,
  ThreeSharing: 3,
  FourSharing: 4,
  FiveSharing: 5,
  SixSharing: 6,
  SevenSharing: 7,
  EightSharing: 8,
};

interface PublicationIssue {
  field: string;
  message: string;
}

export function ListingManagePage() {
  const { propertyId = '' } = useParams();
  const [searchParams] = useSearchParams();

  // Billing links here with ?tab=billing so renewing is one hop, not three.
  const requestedTab = searchParams.get('tab');

  const [property, setProperty] = useState<PropertyDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(
    (['overview', 'rooms', 'photos', 'availability', 'billing'] as const).includes(requestedTab as Tab)
      ? (requestedTab as Tab)
      : 'overview',
  );

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      setProperty(await ownerPropertyApi.get(propertyId));
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [propertyId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) return <TableSkeleton rows={6} columns={3} />;
  if (error) return <ErrorState error={error} onRetry={load} />;
  if (!property) return null;

  const status = statusLabel(property.status);
  const isHome = property.category === 'HomeRental';

  const tabs: [Tab, string][] = [
    ['overview', 'Overview'],
    ...((isHome ? [] : [['rooms', 'Rooms & pricing'] as [Tab, string]]) as [Tab, string][]),
    ['photos', 'Photos'],
    ...((isHome ? [] : [['availability', 'Availability'] as [Tab, string]]) as [Tab, string][]),
    ['billing', isHome ? 'Listing payment' : 'Subscription'],
  ];

  return (
    <>
      <div className="d-flex flex-wrap justify-content-between align-items-start gap-2 mb-3">
        <div>
          {/* An h1: the portal shell no longer carries a page heading, so this is the
              document top heading. The h5 class keeps it visually unchanged. */}
          <h1 className="h5 mb-1">
            {property.title} <span className={`badge bg-${status.variant}`}>{status.text}</span>
          </h1>
          <p className="sf-muted small mb-0">
            {property.businessName} · {property.locality}
            {property.expiresAtUtc && <> · expires {date(property.expiresAtUtc)}</>}
          </p>
        </div>

        <div className="d-flex gap-2">
          <Link to={`/owner/listings/${property.id}/edit`} className="btn btn-sm btn-outline-primary">
            Edit details
          </Link>
          <Link to="/owner/listings" className="btn btn-sm btn-outline-secondary">
            Back
          </Link>
        </div>
      </div>

      <InlineError message={error} />
      <InlineSuccess message={success} />

      {property.statusReason && (
        <div className="alert alert-warning" role="status">
          {property.statusReason}
        </div>
      )}

      <ul className="nav nav-tabs mb-3">
        {tabs.map(([value, label]) => (
          <li className="nav-item" key={value}>
            <button
              type="button"
              className={`nav-link ${tab === value ? 'active' : ''}`}
              onClick={() => setTab(value)}
              aria-current={tab === value ? 'page' : undefined}
            >
              {label}
            </button>
          </li>
        ))}
      </ul>

      {tab === 'overview' && (
        <OverviewTab
          property={property}
          onChanged={load}
          onMessage={setSuccess}
          onError={setError}
          onBillingRequired={() => setTab('billing')}
          onSectionRequired={setTab}
        />
      )}
      {tab === 'rooms' && <RoomsTab property={property} onChanged={load} />}
      {tab === 'photos' && <PhotosTab property={property} onChanged={load} />}
      {tab === 'availability' && <AvailabilityTab property={property} onChanged={load} />}
      {tab === 'billing' && (
        <BillingTab
          property={property}
          onChanged={load}
          onPaymentConfirmed={async () => {
            await load();
            setTab('overview');
          }}
        />
      )}
    </>
  );
}

// =====================================================================================
// Overview
// =====================================================================================

function OverviewTab({
  property,
  onChanged,
  onMessage,
  onError,
  onBillingRequired,
  onSectionRequired,
}: {
  property: PropertyDetail;
  onChanged: () => Promise<void>;
  onMessage: (message: string) => void;
  onError: (error: ApiError) => void;
  onBillingRequired: () => void;
  onSectionRequired: (tab: Tab) => void;
}) {
  const [submitting, setSubmitting] = useState(false);
  const [issues, setIssues] = useState<PublicationIssue[]>([]);

  const submit = async () => {
    setSubmitting(true);
    setIssues([]);

    const localIssues = getPublicationIssues(property);
    if (localIssues.length > 0) {
      setIssues(localIssues);
      onMessage('');
      setSubmitting(false);
      return;
    }

    try {
      const updated = await ownerPropertyApi.submit(property.id);

      await onChanged();

      if (updated.status === 'PendingPayment') {
        onMessage('Your listing details are complete. Complete the payment to continue publication.');
        onBillingRequired();
      } else {
        onMessage('Submitted for review. We will let you know once it is approved.');
      }
    } catch (err) {
      const apiError = toApiError(err);

      // Completeness problems come back as field errors; show them as a checklist.
      const list = Object.entries(apiError.fieldErrors).flatMap(([field, messages]) =>
        messages.map((message) => ({ field: field.split('.')[0], message })),
      );
      if (list.length > 0) {
        setIssues(list);
        onMessage('');
      } else {
        onError(apiError);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const canSubmit = ['Draft', 'Rejected', 'Expired', 'PendingPayment'].includes(property.status);

  return (
    <>
      {issues.length > 0 && (
        <div className="alert alert-warning" role="alert">
          <strong>Complete these details before submitting:</strong>
          <ul className="mb-0 mt-2">
            {issues.map((issue) => {
              const target = publicationTarget(issue.field, property);
              const targetTab = target.tab;
              return (
                <li key={`${issue.field}-${issue.message}`}>
                  {issue.message}{' '}
                  {targetTab ? (
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0 align-baseline"
                      onClick={() => onSectionRequired(targetTab)}
                    >
                      Go to {target.label}
                    </button>
                  ) : (
                    <Link className="btn btn-link btn-sm p-0 align-baseline" to={`/owner/listings/${property.id}/edit`}>
                      Edit listing details
                    </Link>
                  )}
                </li>
              );
            })}
          </ul>
          <p className="small mb-0 mt-2">
            Once all required sections are complete, submit again. If payment is required, you will be taken to the
            {property.category === 'HomeRental' ? ' listing payment' : ' branch subscription'} step.
          </p>
        </div>
      )}

      <div className="row g-3">
        <div className="col-12 col-lg-7">
          <div className="sf-card sf-card-pad">
            <h3 className="sf-section-title">Listing summary</h3>
            <dl className="row mb-0">
              <dt className="col-5 sf-muted fw-normal">Category</dt>
              <dd className="col-7">{humanise(property.category)}</dd>

              <dt className="col-5 sf-muted fw-normal">Address</dt>
              <dd className="col-7">
                {property.addressLine1}, {property.locality} {property.pincode}
              </dd>

              <dt className="col-5 sf-muted fw-normal">Map location</dt>
              <dd className="col-7">
                {property.latitude !== undefined && property.latitude !== null ? (
                  'Set'
                ) : (
                  <span className="text-warning">
                    Not set — this listing will not appear in distance searches
                  </span>
                )}
              </dd>

              <dt className="col-5 sf-muted fw-normal">Photos</dt>
              <dd className="col-7">{property.photos.length}</dd>

              {property.category !== 'HomeRental' && (
                <>
                  <dt className="col-5 sf-muted fw-normal">Room types</dt>
                  <dd className="col-7">{property.roomTypes.length}</dd>
                </>
              )}

              <dt className="col-5 sf-muted fw-normal">Price from</dt>
              <dd className="col-7">{money(property.priceFrom)}</dd>

              <dt className="col-5 sf-muted fw-normal">Published</dt>
              <dd className="col-7">{property.publishedAtUtc ? date(property.publishedAtUtc) : 'Not yet'}</dd>
            </dl>
          </div>
        </div>

        <div className="col-12 col-lg-5">
          <div className="sf-card sf-card-pad">
            <h3 className="sf-section-title">Publishing</h3>

            <p className="sf-muted small">
              A listing goes live after it is paid for and reviewed. You can keep editing it at any point before that.
            </p>

            {canSubmit ? (
              <button type="button" className="btn btn-primary w-100" onClick={submit} disabled={submitting}>
                {submitting ? 'Submitting…' : 'Submit for publication'}
              </button>
            ) : (
              <p className="mb-0">
                This listing is <strong>{statusLabel(property.status).text.toLowerCase()}</strong>.
              </p>
            )}

            {(property.status === 'Active' || property.status === 'ExpiringSoon') && (
              <Link to={`/listing/${property.slug}`} className="btn btn-outline-secondary w-100 mt-2">
                View the public page
              </Link>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function getPublicationIssues(property: PropertyDetail): PublicationIssue[] {
  const issues: PublicationIssue[] = [];

  if ((property.description?.trim().length ?? 0) < 30) {
    issues.push({ field: 'description', message: 'Add a description of at least 30 characters.' });
  }
  if (property.photos.length === 0) {
    issues.push({ field: 'photos', message: 'Add at least one photo before submitting.' });
  }

  if (property.category !== 'HomeRental') {
    const activeRoomTypes = property.roomTypes.filter((roomType) => roomType.isActive);
    if (activeRoomTypes.length === 0) {
      issues.push({ field: 'roomTypes', message: 'Add at least one room type before submitting.' });
    } else {
      const hasValidPrice = activeRoomTypes.some((roomType) =>
        property.category === 'Hotel'
          ? (roomType.pricePerNight ?? 0) > 0
          : (roomType.monthlyRent ?? 0) > 0,
      );

      if (!hasValidPrice) {
        issues.push({
          field: 'roomTypes',
          message: property.category === 'Hotel'
            ? 'Set a nightly price on at least one room type.'
            : 'Set a monthly rent on at least one room type.',
        });
      }
    }
  }

  return issues;
}

function publicationTarget(field: string, property: PropertyDetail): { label: string; tab?: Tab } {
  if (field === 'photos') return { label: 'Photos', tab: 'photos' };
  if (field === 'roomTypes' && property.category !== 'HomeRental') {
    return { label: 'Rooms & pricing', tab: 'rooms' };
  }
  return { label: 'Listing details' };
}

// =====================================================================================
// Rooms
// =====================================================================================

function RoomsTab({ property, onChanged }: { property: PropertyDetail; onChanged: () => Promise<void> }) {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>(property.roomTypes);
  const [editing, setEditing] = useState<RoomType | 'new' | null>(null);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [archiving, setArchiving] = useState<RoomType | null>(null);
  const [busy, setBusy] = useState(false);

  const isHotel = property.category === 'Hotel';

  const reload = useCallback(async () => {
    try {
      setRoomTypes(await ownerPropertyApi.roomTypes(property.id));
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    }
  }, [property.id, onChanged]);

  const archive = async () => {
    if (!archiving) return;

    setBusy(true);

    try {
      await ownerPropertyApi.archiveRoomType(archiving.id);
      setArchiving(null);
      await reload();
    } catch (err) {
      setError(toApiError(err).message);
      setArchiving(null);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="h6 mb-0">{isHotel ? 'Room categories' : 'Sharing options'}</h3>
        <button type="button" className="btn btn-sm btn-primary" onClick={() => setEditing('new')}>
          Add {isHotel ? 'a room category' : 'a sharing option'}
        </button>
      </div>

      <InlineError message={error} />

      {roomTypes.length === 0 && (
        <div className="sf-card sf-card-pad sf-muted">
          No {isHotel ? 'room categories' : 'sharing options'} yet. You need at least one before this listing can be
          published.
        </div>
      )}

      <div className="d-flex flex-column gap-3">
        {roomTypes.map((roomType) => {
          const availability = availabilityLabel(
            roomType.availabilityConfidence,
            roomType.availableUnits,
            roomType.unitLabel,
          );

          return (
            <div className="sf-card sf-card-pad" key={roomType.id}>
              <div className="d-flex flex-wrap justify-content-between gap-2">
                <div>
                  <h4 className="h6 mb-1">{roomType.name}</h4>
                  <p className="sf-muted small mb-1">
                    {isHotel ? money(roomType.pricePerNight) + ' per night' : money(roomType.monthlyRent) + ' per month'}
                    {' · '}
                    {roomType.hasAirConditioning ? 'AC' : 'Non-AC'}
                    {' · '}
                    {humanise(roomType.bathroomType)} bathroom
                  </p>

                  <div className="d-flex flex-wrap gap-2">
                    <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>
                    <span className="sf-chip">
                      {roomType.inventoryMode === 'Detailed'
                        ? 'Counted from individual beds'
                        : 'Counted from the totals you entered'}
                    </span>
                  </div>
                </div>

                <div className="d-flex flex-column gap-2">
                  <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => setEditing(roomType)}>
                    Edit
                  </button>
                  {!isHotel && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => setExpanded(expanded === roomType.id ? null : roomType.id)}
                    >
                      {expanded === roomType.id ? 'Hide rooms' : 'Rooms & beds'}
                    </button>
                  )}
                  <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setArchiving(roomType)}>
                    Remove
                  </button>
                </div>
              </div>

              {expanded === roomType.id && <RoomsPanel roomType={roomType} onChanged={reload} />}
            </div>
          );
        })}
      </div>

      {editing && (
        <RoomTypeDialog
          propertyId={property.id}
          isHotel={isHotel}
          roomType={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={async () => {
            setEditing(null);
            await reload();
          }}
        />
      )}

      <ConfirmDialog
        open={archiving !== null}
        title="Remove this room type?"
        destructive
        busy={busy}
        confirmLabel="Remove"
        message={
          <p className="mb-0">
            <strong>{archiving?.name}</strong> will stop being shown to customers. Any rooms and beds under it are
            kept, so nothing is lost if you add it back.
          </p>
        }
        onConfirm={archive}
        onCancel={() => setArchiving(null)}
      />
    </>
  );
}

function RoomsPanel({ roomType, onChanged }: { roomType: RoomType; onChanged: () => Promise<void> }) {
  const [rooms, setRooms] = useState<Room[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [roomNumber, setRoomNumber] = useState('');
  const [bedCount, setBedCount] = useState(String(roomType.bedsPerRoom ?? 2));
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setRooms(await ownerPropertyApi.rooms(roomType.id));
    } catch (err) {
      setError(toApiError(err).message);
    }
  }, [roomType.id]);

  useEffect(() => {
    void load();
  }, [load]);

  const addRoom = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const count = Number(bedCount) || 1;
    const labels = Array.from({ length: count }, (_, i) => `${roomNumber.trim()}-${String.fromCharCode(65 + i)}`);

    try {
      await ownerPropertyApi.createRoom(roomType.id, { roomNumber: roomNumber.trim(), bedLabels: labels });
      setRoomNumber('');
      await load();
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const cycleBed = async (bedId: string, current: string) => {
    const next = current === 'Vacant' ? 'Occupied' : current === 'Occupied' ? 'OutOfService' : 'Vacant';

    try {
      await ownerPropertyApi.updateBedStatus(bedId, next);
      await load();
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    }
  };

  return (
    <div className="mt-3 pt-3 border-top">
      <InlineError message={error} />

      <p className="sf-muted small">
        Once you list individual rooms, vacancy is counted from the beds below and the totals you typed are no longer
        used. Click a bed to change its status.
      </p>

      {rooms && rooms.length > 0 && (
        <div className="d-flex flex-column gap-2 mb-3">
          {rooms.map((room) => (
            <div className="d-flex flex-wrap align-items-center gap-2" key={room.id}>
              <strong style={{ minWidth: 70 }}>{room.roomNumber}</strong>

              {room.beds.map((bed) => (
                <button
                  type="button"
                  key={bed.id}
                  className={`btn btn-sm ${
                    bed.status === 'Vacant'
                      ? 'btn-outline-success'
                      : bed.status === 'Occupied'
                        ? 'btn-secondary'
                        : 'btn-outline-danger'
                  }`}
                  onClick={() => cycleBed(bed.id, bed.status)}
                  title={`${bed.bedLabel}: ${humanise(bed.status)} — click to change`}
                >
                  {bed.bedLabel.split('-').pop()} · {humanise(bed.status)}
                </button>
              ))}

              <button
                type="button"
                className="btn btn-sm btn-link text-danger"
                onClick={async () => {
                  await ownerPropertyApi.archiveRoom(room.id);
                  await load();
                  await onChanged();
                }}
              >
                Remove room
              </button>
            </div>
          ))}
        </div>
      )}

      <form className="row g-2 align-items-end" onSubmit={addRoom}>
        <div className="col-6 col-sm-4">
          <label className="form-label small mb-1" htmlFor={`room-number-${roomType.id}`}>
            Room number
          </label>
          <input
            id={`room-number-${roomType.id}`}
            className="form-control form-control-sm"
            value={roomNumber}
            onChange={(e) => setRoomNumber(e.target.value)}
            required
          />
        </div>

        <div className="col-6 col-sm-3">
          <label className="form-label small mb-1" htmlFor={`room-beds-${roomType.id}`}>
            Beds in it
          </label>
          <input
            id={`room-beds-${roomType.id}`}
            className="form-control form-control-sm"
            inputMode="numeric"
            value={bedCount}
            onChange={(e) => setBedCount(e.target.value)}
          />
        </div>

        <div className="col-12 col-sm-3">
          <button type="submit" className="btn btn-sm btn-outline-primary w-100" disabled={busy || !roomNumber.trim()}>
            Add room
          </button>
        </div>
      </form>
    </div>
  );
}

function RoomTypeDialog({
  propertyId,
  isHotel,
  roomType,
  onClose,
  onSaved,
}: {
  propertyId: string;
  isHotel: boolean;
  roomType: RoomType | null;
  onClose: () => void;
  onSaved: () => Promise<void>;
}) {
  const [name, setName] = useState(roomType?.name ?? '');
  const [description, setDescription] = useState(roomType?.description ?? '');
  const [sharingType, setSharingType] = useState(roomType?.sharingType ?? 'TwoSharing');
  const [hotelRoomCategory, setHotelCategory] = useState(roomType?.hotelRoomCategory ?? 'Double');
  const [monthlyRent, setMonthlyRent] = useState(roomType?.monthlyRent?.toString() ?? '');
  const [securityDeposit, setDeposit] = useState(roomType?.securityDeposit?.toString() ?? '');
  const [pricePerNight, setPricePerNight] = useState(roomType?.pricePerNight?.toString() ?? '');
  const [taxPercentage, setTax] = useState(roomType?.taxPercentage?.toString() ?? '12');
  const [bedConfiguration, setBedConfiguration] = useState(roomType?.bedConfiguration ?? '');
  const [maxOccupancy, setMaxOccupancy] = useState(roomType?.maxOccupancy?.toString() ?? '2');
  const [totalRooms, setTotalRooms] = useState(roomType?.totalRooms?.toString() ?? '');
  const [availableRooms, setAvailableRooms] = useState('');
  const [totalBeds, setTotalBeds] = useState(() => {
    const savedTotal = roomType?.totalBeds ?? roomType?.totalUnits;
    return savedTotal?.toString() ?? '';
  });
  const [occupiedBeds, setOccupiedBeds] = useState(() => {
    if (roomType?.occupiedBeds != null) return roomType.occupiedBeds.toString();
    if (roomType?.totalUnits == null) return '';
    return Math.max(roomType.totalUnits - (roomType.availableUnits ?? roomType.totalUnits), 0).toString();
  });
  const [hasAirConditioning, setAc] = useState(roomType?.hasAirConditioning ?? false);
  const [bathroomType, setBathroomType] = useState(roomType?.bathroomType ?? 'Shared');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isDetailed = roomType?.inventoryMode === 'Detailed';

  const number = (value: string) => (value.trim() === '' ? undefined : Number(value));

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setError(null);

    const body = isHotel
      ? {
          name: name.trim(),
          description: description.trim() || undefined,
          hotelRoomCategory,
          pricePerNight: number(pricePerNight),
          taxPercentage: number(taxPercentage),
          bedConfiguration: bedConfiguration.trim(),
          maxOccupancy: number(maxOccupancy),
          totalRooms: number(totalRooms),
          availableRooms: number(availableRooms),
          hasAirConditioning,
          bathroomType,
          isActive: true,
        }
      : {
          name: name.trim(),
          description: description.trim() || undefined,
          sharingType,
          bedsPerRoom: sharingBedCounts[sharingType],
          monthlyRent: number(monthlyRent),
          securityDeposit: number(securityDeposit),
          totalRooms: number(totalRooms),
          totalBeds: number(totalBeds),
          occupiedBeds: number(occupiedBeds),
          hasAirConditioning,
          bathroomType,
          isActive: true,
        };

    try {
      if (roomType) await ownerPropertyApi.updateRoomType(roomType.id, body);
      else await ownerPropertyApi.createRoomType(propertyId, body);

      await onSaved();
    } catch (err) {
      setError(toApiError(err).message);
      setBusy(false);
    }
  };

  return (
    <>
      <div className="modal-backdrop show" />
      <div className="modal d-block" role="dialog" aria-modal="true" aria-labelledby="roomtype-title">
        <div className="modal-dialog modal-dialog-centered modal-lg">
          <div className="modal-content">
            <form onSubmit={submit} noValidate>
              <div className="modal-header">
                <h2 className="modal-title h5" id="roomtype-title">
                  {roomType ? 'Edit' : 'Add'} {isHotel ? 'room category' : 'sharing option'}
                </h2>
              </div>

              <div className="modal-body">
                <InlineError message={error} />

                <Field label="Name" htmlFor="rt-name" required>
                  <input
                    id="rt-name"
                    className="form-control"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    placeholder={isHotel ? 'Deluxe double' : 'Two sharing (AC)'}
                  />
                </Field>

                {isHotel ? (
                  <>
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <Field label="Room category" htmlFor="rt-hotel-category" required>
                          <select
                            id="rt-hotel-category"
                            className="form-select"
                            value={hotelRoomCategory}
                            onChange={(e) => setHotelCategory(e.target.value as never)}
                          >
                            {['Single', 'Double', 'TwinBed', 'Deluxe', 'Suite', 'Family', 'Other'].map((value) => (
                              <option key={value} value={value}>
                                {humanise(value)}
                              </option>
                            ))}
                          </select>
                        </Field>
                      </div>

                      <div className="col-6 col-md-3">
                        <Field label="Price per night" htmlFor="rt-price" required>
                          <input
                            id="rt-price"
                            className="form-control"
                            inputMode="numeric"
                            value={pricePerNight}
                            onChange={(e) => setPricePerNight(e.target.value)}
                            required
                          />
                        </Field>
                      </div>

                      <div className="col-6 col-md-3">
                        <Field label="Tax %" htmlFor="rt-tax">
                          <input
                            id="rt-tax"
                            className="form-control"
                            inputMode="decimal"
                            value={taxPercentage}
                            onChange={(e) => setTax(e.target.value)}
                          />
                        </Field>
                      </div>
                    </div>

                    <Field
                      label="Bed configuration"
                      htmlFor="rt-beds"
                      required
                      hint="What is actually in the room, for example 1 King Bed or 2 Single Beds."
                    >
                      <input
                        id="rt-beds"
                        className="form-control"
                        value={bedConfiguration}
                        onChange={(e) => setBedConfiguration(e.target.value)}
                        required
                      />
                    </Field>

                    <div className="row">
                      <div className="col-6 col-md-4">
                        <Field
                          label="Maximum guests"
                          htmlFor="rt-occupancy"
                          required
                          hint="Separate from the bed layout above."
                        >
                          <input
                            id="rt-occupancy"
                            className="form-control"
                            inputMode="numeric"
                            value={maxOccupancy}
                            onChange={(e) => setMaxOccupancy(e.target.value)}
                            required
                          />
                        </Field>
                      </div>

                      <div className="col-6 col-md-4">
                        <Field label="Total rooms" htmlFor="rt-total-rooms">
                          <input
                            id="rt-total-rooms"
                            className="form-control"
                            inputMode="numeric"
                            value={totalRooms}
                            onChange={(e) => setTotalRooms(e.target.value)}
                          />
                        </Field>
                      </div>

                      <div className="col-6 col-md-4">
                        <Field label="Available now" htmlFor="rt-available-rooms">
                          <input
                            id="rt-available-rooms"
                            className="form-control"
                            inputMode="numeric"
                            value={availableRooms}
                            onChange={(e) => setAvailableRooms(e.target.value)}
                          />
                        </Field>
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="row">
                      <div className="col-12 col-md-6">
                        <Field label="Sharing" htmlFor="rt-sharing" required>
                          <select
                            id="rt-sharing"
                            className="form-select"
                            value={sharingType}
                            onChange={(e) => setSharingType(e.target.value as never)}
                          >
                            <option value="Single">Single occupancy</option>
                            <option value="TwoSharing">2 sharing</option>
                            <option value="ThreeSharing">3 sharing</option>
                            <option value="FourSharing">4 sharing</option>
                            <option value="FiveSharing">5 sharing</option>
                            <option value="SixSharing">6 sharing</option>
                            <option value="SevenSharing">7 sharing</option>
                            <option value="EightSharing">8 sharing</option>
                          </select>
                        </Field>
                      </div>

                      <div className="col-6 col-md-3">
                        <Field label="Monthly rent" htmlFor="rt-rent" required>
                          <input
                            id="rt-rent"
                            className="form-control"
                            inputMode="numeric"
                            value={monthlyRent}
                            onChange={(e) => setMonthlyRent(e.target.value)}
                            required
                          />
                        </Field>
                      </div>

                      <div className="col-6 col-md-3">
                        <Field label="Deposit" htmlFor="rt-deposit">
                          <input
                            id="rt-deposit"
                            className="form-control"
                            inputMode="numeric"
                            value={securityDeposit}
                            onChange={(e) => setDeposit(e.target.value)}
                          />
                        </Field>
                      </div>
                    </div>

                    <div className="row">
                      <div className="col-4">
                        <Field label="Rooms" htmlFor="rt-rooms">
                          <input
                            id="rt-rooms"
                            className="form-control"
                            inputMode="numeric"
                            value={totalRooms}
                            onChange={(e) => setTotalRooms(e.target.value)}
                          />
                        </Field>
                      </div>

                      <div className="col-4">
                        <Field label="Total beds" htmlFor="rt-total-beds">
                          <input
                            id="rt-total-beds"
                            className="form-control"
                            inputMode="numeric"
                            value={totalBeds}
                            onChange={(e) => setTotalBeds(e.target.value)}
                            disabled={isDetailed}
                          />
                        </Field>
                      </div>

                      <div className="col-4">
                        <Field label="Occupied" htmlFor="rt-occupied">
                          <input
                            id="rt-occupied"
                            className="form-control"
                            inputMode="numeric"
                            value={occupiedBeds}
                            onChange={(e) => setOccupiedBeds(e.target.value)}
                            disabled={isDetailed}
                          />
                        </Field>
                      </div>
                    </div>

                    {isDetailed && (
                      <div className="alert alert-info py-2 px-3 small">
                        This room type has individual beds, so vacancy is counted from them. Update the bed statuses
                        instead of these totals.
                      </div>
                    )}
                  </>
                )}

                <Field label="Description" htmlFor="rt-description">
                  <textarea
                    id="rt-description"
                    className="form-control"
                    rows={2}
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    maxLength={2000}
                  />
                </Field>

                <div className="row">
                  <div className="col-12 col-md-6">
                    <Field label="Bathroom" htmlFor="rt-bathroom">
                      <select
                        id="rt-bathroom"
                        className="form-select"
                        value={bathroomType}
                        onChange={(e) => setBathroomType(e.target.value as never)}
                      >
                        <option value="Attached">Attached</option>
                        <option value="Shared">Shared</option>
                        <option value="Common">Common</option>
                      </select>
                    </Field>
                  </div>
                  <div className="col-12 col-md-6 d-flex align-items-center">
                    <Checkbox id="rt-ac" label="Air conditioned" checked={hasAirConditioning} onChange={setAc} />
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn btn-outline-secondary" onClick={onClose} disabled={busy}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={busy}>
                  {busy ? 'Saving…' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
}

// =====================================================================================
// Photos
// =====================================================================================

function PhotosTab({ property, onChanged }: { property: PropertyDetail; onChanged: () => Promise<void> }) {
  const [photos, setPhotos] = useState<Photo[]>(property.photos);
  const [category, setCategory] = useState('Exterior');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<Photo | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      setPhotos(await ownerPropertyApi.photos(property.id));
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    }
  }, [property.id, onChanged]);

  const upload = async (files: FileList | null) => {
    if (!files || files.length === 0) return;

    setUploading(true);
    setError(null);

    // Uploaded one at a time so a single rejected file does not lose the rest.
    for (const file of Array.from(files)) {
      try {
        await ownerPropertyApi.uploadPhoto(property.id, file, category);
      } catch (err) {
        setError(`${file.name}: ${toApiError(err).message}`);
      }
    }

    setUploading(false);
    if (fileInput.current) fileInput.current.value = '';
    await reload();
  };

  return (
    <>
      <InlineError message={error} />

      <div className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">Add photos</h3>

        <div className="row g-2 align-items-end">
          <div className="col-12 col-sm-4">
            <label className="form-label small mb-1" htmlFor="photo-category">
              What do these show?
            </label>
            <select
              id="photo-category"
              className="form-select form-select-sm"
              value={category}
              onChange={(e) => setCategory(e.target.value)}
            >
              {['Exterior', 'Bedroom', 'Bathroom', 'Kitchen', 'CommonArea', 'Facilities', 'Other'].map((value) => (
                <option key={value} value={value}>
                  {humanise(value)}
                </option>
              ))}
            </select>
          </div>

          <div className="col-12 col-sm-8">
            <label className="form-label small mb-1" htmlFor="photo-file">
              Choose images
            </label>
            <input
              id="photo-file"
              ref={fileInput}
              type="file"
              className="form-control form-control-sm"
              accept="image/jpeg,image/png,image/webp,image/heic"
              multiple
              onChange={(e) => upload(e.target.files)}
              disabled={uploading}
            />
          </div>
        </div>

        <p className="form-text mb-0">
          JPEG, PNG, WebP or HEIC. Images are resized and re-saved on our side, which also strips location data from
          them.
        </p>

        {uploading && <p className="mb-0 mt-2">Uploading…</p>}
      </div>

      {photos.length === 0 ? (
        <div className="sf-card sf-card-pad sf-muted">
          No photos yet. You need at least one before this listing can be published.
        </div>
      ) : (
        <div className="row g-3">
          {photos.map((photo) => (
            <div className="col-6 col-md-4 col-lg-3" key={photo.id}>
              <div className="sf-listing-card">
                <div className="sf-listing-card__media">
                  <img src={photo.thumbnailUrl ?? photo.url} alt={photo.altText ?? ''} />
                  {photo.isPrimary && <span className="sf-featured-flag">Cover</span>}
                </div>

                <div className="sf-listing-card__body">
                  <span className="sf-chip align-self-start">{humanise(photo.category)}</span>

                  <div className="d-flex gap-1 mt-2">
                    {!photo.isPrimary && (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary flex-grow-1"
                        onClick={async () => {
                          await ownerPropertyApi.updatePhoto(photo.id, { isPrimary: true });
                          await reload();
                        }}
                      >
                        Make cover
                      </button>
                    )}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-danger"
                      onClick={() => setDeleting(photo)}
                      aria-label="Delete photo"
                    >
                      <IconClose />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={deleting !== null}
        title="Delete this photo?"
        destructive
        confirmLabel="Delete"
        message={<p className="mb-0">This cannot be undone.</p>}
        onConfirm={async () => {
          if (deleting) await ownerPropertyApi.deletePhoto(deleting.id);
          setDeleting(null);
          await reload();
        }}
        onCancel={() => setDeleting(null)}
      />
    </>
  );
}

// =====================================================================================
// Availability
// =====================================================================================

function AvailabilityTab({ property, onChanged }: { property: PropertyDetail; onChanged: () => Promise<void> }) {
  const [roomTypes, setRoomTypes] = useState<RoomType[]>(property.roomTypes);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const isStale = property.availabilityConfidence === 'Unconfirmed';

  const confirmAll = async () => {
    setBusy(true);
    setError(null);

    try {
      setRoomTypes(await ownerPropertyApi.updateAvailability(property.id, { notes: 'Confirmed as still accurate.' }));
      setSuccess('Availability confirmed. Customers can see your numbers again.');
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  const updateCounts = async (roomTypeId: string, availableUnits: number, totalUnits: number) => {
    setBusy(true);
    setError(null);

    try {
      setRoomTypes(await ownerPropertyApi.updateAvailability(property.id, { roomTypeId, availableUnits, totalUnits }));
      setSuccess('Availability updated.');
      await onChanged();
    } catch (err) {
      setError(toApiError(err).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <InlineError message={error} />
      <InlineSuccess message={success} />

      <div className="sf-card sf-card-pad mb-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <div>
            <h3 className="sf-section-title mb-1">Availability</h3>
            <p className="sf-muted small mb-0">
              Last confirmed {relativeTime(property.lastAvailabilityUpdateUtc)}.
            </p>
          </div>

          <button type="button" className="btn btn-primary" onClick={confirmAll} disabled={busy}>
            {busy ? 'Saving…' : 'Confirm it is still accurate'}
          </button>
        </div>

        {isStale && (
          <div className="alert alert-warning mt-3 mb-0" role="status">
            Customers currently see <strong>&ldquo;Availability unconfirmed&rdquo;</strong> instead of your numbers,
            because this listing has not been updated recently. Confirm it above to show live figures again.
          </div>
        )}
      </div>

      <div className="d-flex flex-column gap-3">
        {roomTypes.map((roomType) => (
          <AvailabilityRow key={roomType.id} roomType={roomType} busy={busy} onUpdate={updateCounts} />
        ))}
      </div>
    </>
  );
}

function AvailabilityRow({
  roomType,
  busy,
  onUpdate,
}: {
  roomType: RoomType;
  busy: boolean;
  onUpdate: (roomTypeId: string, availableUnits: number, totalUnits: number) => Promise<void>;
}) {
  const [available, setAvailable] = useState(String(roomType.availableUnits ?? 0));
  const [total, setTotal] = useState(String(roomType.totalUnits ?? 0));

  const detailed = roomType.inventoryMode === 'Detailed';
  const availability = availabilityLabel(roomType.availabilityConfidence, roomType.availableUnits, roomType.unitLabel);

  return (
    <div className="sf-card sf-card-pad">
      <div className="d-flex flex-wrap justify-content-between align-items-end gap-3">
        <div>
          <h4 className="h6 mb-1">{roomType.name}</h4>
          <span className={`sf-pill sf-pill--${availability.tone}`}>{availability.text}</span>
        </div>

        {detailed ? (
          <p className="sf-muted small mb-0" style={{ maxWidth: 420 }}>
            This room type tracks individual {roomType.unitLabel}. Change their status under <strong>Rooms &amp;
            pricing</strong> and the count here updates itself.
          </p>
        ) : (
          <div className="d-flex gap-2 align-items-end">
            <div>
              <label className="form-label small mb-1" htmlFor={`avail-${roomType.id}`}>
                Available {roomType.unitLabel}
              </label>
              <input
                id={`avail-${roomType.id}`}
                className="form-control form-control-sm"
                inputMode="numeric"
                style={{ width: 90 }}
                value={available}
                onChange={(e) => setAvailable(e.target.value)}
              />
            </div>

            <div>
              <label className="form-label small mb-1" htmlFor={`total-${roomType.id}`}>
                Total
              </label>
              <input
                id={`total-${roomType.id}`}
                className="form-control form-control-sm"
                inputMode="numeric"
                style={{ width: 90 }}
                value={total}
                onChange={(e) => setTotal(e.target.value)}
              />
            </div>

            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              disabled={busy}
              onClick={() => onUpdate(roomType.id, Number(available) || 0, Number(total) || 0)}
            >
              Update
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// =====================================================================================
// Billing
// =====================================================================================

function BillingTab({
  property,
  onChanged,
  onPaymentConfirmed,
}: {
  property: PropertyDetail;
  onChanged: () => Promise<void>;
  onPaymentConfirmed: () => Promise<void>;
}) {
  const [requirement, setRequirement] = useState<PaymentRequirement | null>(null);
  const [plans, setPlans] = useState<ListingPlan[]>([]);
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showBranchCheckout, setShowBranchCheckout] = useState(false);
  const [showFeaturedCheckout, setShowFeaturedCheckout] = useState(false);

  const isHome = property.category === 'HomeRental';

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    Promise.all([
      billingApi.requirement(property.category),
      billingApi.plans(property.category),
      billingApi.subscriptions(property.businessId),
    ])
      .then(([found, list, currentSubscriptions]) => {
        if (cancelled) return;
        setRequirement(found);
        setSubscriptions(currentSubscriptions.filter((subscription) => subscription.propertyId === property.id));

        if (!found.paymentRequired || (!found.gatewayIntegrated && !found.simulatedCheckoutAvailable)) {
          setPlans([]);
          return;
        }

        setPlans(list.filter((plan) =>
          plan.billingType === (isHome ? 'PerListing' : 'BranchSubscription')
          || plan.billingType === 'FeaturedPlacement',
        ));
      })
      .catch((err) => !cancelled && setError(toApiError(err).message))
      .finally(() => !cancelled && setLoading(false));

    return () => {
      cancelled = true;
    };
  }, [property.businessId, property.category, property.id, isHome]);

  if (loading) return <TableSkeleton rows={3} />;

  const branchPlans = plans.filter((plan) => plan.billingType !== 'FeaturedPlacement');
  const featuredPlans = plans.filter((plan) => plan.billingType === 'FeaturedPlacement');
  const currentSubscription = subscriptions.find((subscription) =>
    ['Active', 'ExpiringSoon', 'GracePeriod'].includes(subscription.status),
  );
  const latestSubscription = currentSubscription ?? subscriptions[0];
  const canCheckout = requirement?.paymentRequired
    && (requirement.gatewayIntegrated || requirement.simulatedCheckoutAvailable);

  return (
    <>
      <InlineError message={error} />

      {property.status === 'PendingPayment' && (
        <div className="alert alert-warning" role="status">
          <strong>Payment is the remaining step.</strong>{' '}
          Your listing details have been submitted. Complete the {isHome ? 'listing payment' : 'branch subscription'}{' '}
          below to continue to publication review.
        </div>
      )}

      <div className="sf-card sf-card-pad mb-3">
        <h3 className="sf-section-title">{isHome ? 'Listing payment' : 'Branch subscription'}</h3>

        <p className="sf-muted small">
          {isHome
            ? 'Each home listing is paid for on its own. Paying for this one does not affect your other listings.'
            : 'Each branch carries its own subscription. If this one lapses, your other branches stay live.'}
        </p>

        {currentSubscription ? (
          <div className="alert alert-success" role="status">
            <strong>Already subscribed:</strong> {currentSubscription.planName}
            <br />
            Renews on <strong>{date(currentSubscription.expiryUtc)}</strong>.
          </div>
        ) : latestSubscription?.status === 'Expired' ? (
          <div className="alert alert-warning" role="status">
            Previous subscription: {latestSubscription.planName}. It expired on{' '}
            <strong>{date(latestSubscription.expiryUtc)}</strong>.
          </div>
        ) : property.expiresAtUtc ? (
          <p className="mb-3">
            Currently paid until <strong>{date(property.expiresAtUtc)}</strong>.
          </p>
        ) : null}

        {requirement && !requirement.paymentRequired ? (
          <FreeCategoryNotice requirement={requirement} />
        ) : requirement && !requirement.gatewayIntegrated && !requirement.simulatedCheckoutAvailable ? (
          <GatewayUnavailableNotice requirement={requirement} />
        ) : branchPlans.length === 0 ? (
          <p className="sf-muted mb-0">No plans are available for this category yet.</p>
        ) : currentSubscription ? (
          <>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowBranchCheckout((show) => !show)}
            >
              {showBranchCheckout ? 'Hide renewal plans' : 'Renew early'}
            </button>
            {showBranchCheckout && (
              <div className="mt-3">
                <Checkout
                  propertyId={property.id}
                  propertyName={property.title}
                  requirement={requirement!}
                  plans={branchPlans}
                  onActivated={onPaymentConfirmed}
                />
              </div>
            )}
          </>
        ) : (
          <Checkout
            propertyId={property.id}
            propertyName={property.title}
            requirement={requirement!}
            plans={branchPlans}
            onActivated={onPaymentConfirmed}
          />
        )}
      </div>

      <div className="sf-card sf-card-pad">
        <h3 className="sf-section-title">Featured placement</h3>
        <p className="sf-muted small">
          Feature this listing to give it additional visibility in customer search results.
        </p>

        {property.isFeatured ? (
          <div className="alert alert-success" role="status">
            <strong>Featured placement active</strong>
            {property.featuredUntilUtc && (
              <>
                {' '}until <strong>{date(property.featuredUntilUtc)}</strong>
              </>
            )}
            .
          </div>
        ) : (
          <p className="sf-muted">This listing is not currently featured.</p>
        )}

        {featuredPlans.length === 0 ? (
          <p className="sf-muted mb-0">No featured-placement plans are available for this category.</p>
        ) : !canCheckout ? (
          <p className="sf-muted mb-0">Featured placement checkout is currently unavailable.</p>
        ) : (
          <>
            <button
              type="button"
              className="btn btn-outline-primary"
              onClick={() => setShowFeaturedCheckout((show) => !show)}
            >
              {showFeaturedCheckout
                ? 'Hide featured plans'
                : property.isFeatured
                  ? 'Extend featured placement'
                  : 'Feature this listing'}
            </button>
            {showFeaturedCheckout && (
              <div className="mt-3">
                <Checkout
                  propertyId={property.id}
                  propertyName={property.title}
                  requirement={requirement!}
                  plans={featuredPlans}
                  onActivated={onChanged}
                />
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

/** Payment is switched off for this category, so there is nothing to pay. */
function FreeCategoryNotice({ requirement }: { requirement: PaymentRequirement }) {
  return (
    <div className="alert alert-success mb-0" role="status">
      <p className="mb-2">{requirement.message}</p>
      <p className="small mb-0">
        Submit the listing when it is ready and our team will review it. If payment is reintroduced later, listings
        already published stay published until they expire.
      </p>
    </div>
  );
}

/**
 * Payment is required, but no real provider is wired up yet. The listing cannot be
 * activated as a paid listing, and we say so rather than offering a button that pretends.
 */
function GatewayUnavailableNotice({ requirement }: { requirement: PaymentRequirement }) {
  return (
    <div className="alert alert-warning mb-0" role="status">
      <p className="mb-2">{requirement.message}</p>
      <p className="small mb-0">
        The configured price for this category is <strong>{money(requirement.totalAmount)}</strong> (
        {money(requirement.amount)} + {requirement.taxPercentage}% tax)
        {requirement.billingPeriod === 'Monthly'
          ? ` per branch, for ${requirement.listingValidityDays} days`
          : ` per listing, for ${requirement.listingValidityDays} days`}
        . Nothing has been charged and no payment record has been created.
      </p>
    </div>
  );
}
